
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useNotify } from '@/hooks/useNotify';
import { Trash2, Plus, Edit, Save, X, Check, LockIcon } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

interface Unit {
  id: string;
  name: string;
  symbol: string;
  description: string | null;
  is_default: boolean;
  company_id: string;
}

const UnitsSettings = () => {
  const { user, profile } = useAuth();
  const notify = useNotify();
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [newUnit, setNewUnit] = useState({ name: '', symbol: '', description: '', is_default: false });
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);

  useEffect(() => {
    if (profile?.company_id) {
      fetchUnits();
    } else {
      setLoading(false);
    }
  }, [profile]);

  const fetchUnits = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .rpc('get_all_units', { user_company_id: profile?.company_id })
        .order('is_default', { ascending: false })
        .order('name', { ascending: true });

      if (error) throw error;
      setUnits(data || []);
    } catch (error) {
      console.error('Error fetching units:', error);
      notify.error(error);
    } finally {
      setLoading(false);
    }
  };

  const addUnit = async () => {
    try {
      if (!newUnit.name.trim() || !newUnit.symbol.trim()) {
        notify.warning("Required Fields", "Unit name and symbol are required.");
        return;
      }

      if (!profile?.company_id) {
        notify.warning("Missing Company", "You must be associated with a company to add a unit.");
        return;
      }

      if (newUnit.is_default && profile.company_id) {
        await supabase
          .from('line_item_units')
          .update({ is_default: false })
          .eq('company_id', profile.company_id)
          .not('is_default', 'eq', true);
      }

      const { data, error } = await supabase
        .from('line_item_units')
        .insert({
          name: newUnit.name.trim(),
          symbol: newUnit.symbol.trim(),
          description: newUnit.description || null,
          is_default: false,
          company_id: profile.company_id
        })
        .select();

      if (error) throw error;

      if (data) {
        fetchUnits();
        setNewUnit({ name: '', symbol: '', description: '', is_default: false });
        notify.success("Unit added successfully");
      }
    } catch (error) {
      console.error('Error adding unit:', error);
      notify.error(error);
    }
  };

  const startEdit = (unit: Unit) => {
    if (unit.is_default && unit.company_id !== profile?.company_id) {
      notify.warning("Cannot edit default unit", "System default units cannot be modified.");
      return;
    }
    
    setEditingUnit({ ...unit });
  };

  const cancelEdit = () => {
    setEditingUnit(null);
  };

  const saveEdit = async () => {
    try {
      if (!editingUnit || !editingUnit.name.trim() || !editingUnit.symbol.trim()) return;
      
      if (!profile?.company_id) {
        notify.warning("Missing Company", "You must be associated with a company to update a unit.");
        return;
      }

      if (editingUnit.is_default && editingUnit.company_id !== profile.company_id) {
        notify.warning("Cannot edit default unit", "System default units cannot be modified.");
        return;
      }

      if (editingUnit.is_default) {
        await supabase
          .from('line_item_units')
          .update({ is_default: false })
          .neq('id', editingUnit.id)
          .eq('company_id', profile.company_id);
      }

      const { data, error } = await supabase
        .from('line_item_units')
        .update({
          name: editingUnit.name.trim(),
          symbol: editingUnit.symbol.trim(),
          description: editingUnit.description,
          is_default: editingUnit.is_default
        })
        .eq('id', editingUnit.id)
        .select();

      if (error) throw error;

      if (data) {
        fetchUnits();
        setEditingUnit(null);
        notify.success("Unit updated successfully");
      }
    } catch (error) {
      console.error('Error updating unit:', error);
      notify.error(error);
    }
  };

  const deleteUnit = async (id: string, isDefault: boolean) => {
    if (isDefault) {
      notify.warning("Cannot delete default unit", "Default units cannot be removed.");
      return;
    }
    
    try {
      const { error } = await supabase
        .from('line_item_units')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setUnits(units.filter(u => u.id !== id));
      notify.success("Unit deleted successfully");
    } catch (error) {
      console.error('Error deleting unit:', error);
      notify.error(error);
    }
  };

  if (!profile?.company_id) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Line Item Units</CardTitle>
          <CardDescription>Manage measurement units for your line items</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-amber-500">You must be associated with a company to manage line item units.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Line Item Units</CardTitle>
        <CardDescription>Manage measurement units for your line items</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Add new unit form */}
          <div className="flex flex-col gap-3 sm:grid sm:grid-cols-12 sm:gap-4 mb-6">
            <div className="sm:col-span-3">
              <Input 
                placeholder="Unit name (e.g., Meter)" 
                value={newUnit.name}
                onChange={(e) => setNewUnit({...newUnit, name: e.target.value})}
              />
            </div>
            <div className="sm:col-span-2">
              <Input 
                placeholder="Symbol (e.g., m)" 
                value={newUnit.symbol}
                onChange={(e) => setNewUnit({...newUnit, symbol: e.target.value})}
              />
            </div>
            <div className="sm:col-span-4">
              <Input 
                placeholder="Description (optional)" 
                value={newUnit.description}
                onChange={(e) => setNewUnit({...newUnit, description: e.target.value})}
              />
            </div>
            <div className="sm:col-span-1 flex items-center">
              <div className="flex items-center space-x-2">
                <Switch 
                  id="new-unit-default" 
                  checked={newUnit.is_default}
                  onCheckedChange={(checked) => setNewUnit({...newUnit, is_default: checked})}
                />
                <Label htmlFor="new-unit-default">Default</Label>
              </div>
            </div>
            <div className="sm:col-span-2">
              <Button 
                onClick={addUnit} 
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
          </div>

          {/* Units list */}
          <div className="space-y-2">
            {loading ? (
              <p className="text-center py-4 text-muted-foreground">Loading units...</p>
            ) : units.length === 0 ? (
              <p className="text-center py-4 text-muted-foreground">No units found. Add your first unit above.</p>
            ) : (
              <>
                {units.map((unit) => {
                  const isSystemDefault = unit.is_default && unit.company_id !== profile.company_id;
                  return (
                    <div key={unit.id} className="flex flex-col gap-3 p-3 border rounded-md sm:grid sm:grid-cols-12 sm:gap-4 sm:items-center">
                      {editingUnit && editingUnit.id === unit.id ? (
                        <>
                          <div className="sm:col-span-3">
                            <Input 
                              value={editingUnit.name}
                              onChange={(e) => setEditingUnit({...editingUnit, name: e.target.value})}
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <Input 
                              value={editingUnit.symbol}
                              onChange={(e) => setEditingUnit({...editingUnit, symbol: e.target.value})}
                            />
                          </div>
                          <div className="sm:col-span-4">
                            <Input 
                              value={editingUnit.description || ''}
                              onChange={(e) => setEditingUnit({...editingUnit, description: e.target.value})}
                              placeholder="Description (optional)"
                            />
                          </div>
                          <div className="sm:col-span-1 flex items-center">
                            <div className="flex items-center space-x-2">
                              <Switch 
                                id={`edit-unit-default-${unit.id}`} 
                                checked={editingUnit.is_default}
                                onCheckedChange={(checked) => setEditingUnit({...editingUnit, is_default: checked})}
                              />
                              <Label htmlFor={`edit-unit-default-${unit.id}`}>Default</Label>
                            </div>
                          </div>
                          <div className="sm:col-span-2 flex gap-2 justify-end">
                            <Button size="icon" variant="outline" onClick={cancelEdit}>
                              <X className="h-4 w-4" />
                            </Button>
                            <Button size="icon" onClick={saveEdit}>
                              <Save className="h-4 w-4" />
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="sm:col-span-3 font-medium flex items-center gap-2">
                            {unit.name}
                            {isSystemDefault && (
                              <Badge variant="secondary" className="flex items-center gap-1">
                                <LockIcon className="h-3 w-3" />
                                Default
                              </Badge>
                            )}
                          </div>
                          <div className="sm:col-span-2 font-mono">
                            {unit.symbol}
                          </div>
                          <div className="sm:col-span-4 text-muted-foreground">
                            {unit.description || <em>No description</em>}
                          </div>
                          <div className="sm:col-span-1 flex sm:justify-center">
                            {unit.is_default && <Check className="h-4 w-4 text-green-500" />}
                          </div>
                          <div className="sm:col-span-2 flex gap-2 justify-end">
                            <Button 
                              size="icon" 
                              variant="outline" 
                              onClick={() => startEdit(unit)}
                              disabled={isSystemDefault}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="icon" 
                              variant="outline" 
                              onClick={() => deleteUnit(unit.id, isSystemDefault)}
                              disabled={isSystemDefault}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default UnitsSettings;
